import axios from 'axios';

// SEC-06: auth now lives in an httpOnly session cookie the browser manages
// for us — there is no token in JS to read or attach anymore.
// `withCredentials` makes axios send that cookie; the X-Requested-With
// header is a CSRF defense-in-depth check the backend requires on every
// mutating request (see backend/middleware/auth.js) — a cross-site page
// can trigger a simple form POST, but it can't set a custom header without
// tripping CORS, so this proves the request came from our own JS.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true,
  headers: {
    common: { 'X-Requested-With': 'XMLHttpRequest' },
  },
});

api.interceptors.request.use((config) => {
  // Let the browser set multipart boundary — manual Content-Type breaks file uploads
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (config.headers) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
  }

  return config;
});

// Auto-redirect to login on 401 — but NOT for the /auth/me "am I signed in?"
// check itself (a 401 there is the normal, expected answer for a logged-out
// visitor, not a session that died mid-use) and NOT if we're already on
// /login — otherwise that 401 triggers a hard reload, which re-mounts the
// app, which calls /auth/me again, which 401s again: an infinite reload loop.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const isAuthCheck = err.config?.url?.includes('/auth/me');
    const onLoginPage = window.location.pathname.startsWith('/login');
    if (err.response?.status === 401 && !isAuthCheck && !onLoginPage) {
      // Clear the rejected session first — leaving it behind means the app
      // still believes it is signed in and keeps sending a dead token.
      ['tychi_token', 'tychi_user', 'tychi_perms', 'tychi_view_mode']
        .forEach((k) => { try { localStorage.removeItem(k); } catch { /* ignore */ } });
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;
